import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from './ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const SESSION_KEY = 'pzv_popup_dismissed';   // dismissed for this tab session
const DAY_KEY     = 'pzv_popup_day';         // dismissed date (YYYY-MM-DD)

const SitePopup = () => {
  const { t } = useTranslation();
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/settings`);
        const settings = await res.json();
        const p = settings?.popup;
        if (!p?.enabled || !p?.title) return;

        // Unified rule: max 1× per session AND max 1× per calendar day.
        // Both keys are checked regardless of the legacy `display_mode` admin
        // field so an anonymous visitor sees the popup only once, then never
        // again during the same day even if they open new tabs.
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        if (sessionStorage.getItem(SESSION_KEY)) return;
        if (localStorage.getItem(DAY_KEY) === today) return;

        setPopup(p);
        setTimeout(() => setVisible(true), 800);
      } catch { /* silent */ }
    };
    fetchSettings();
  }, []);

  const dismiss = () => {
    setVisible(false);
    // Persist BOTH markers so the popup respects the two rules simultaneously.
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
      localStorage.setItem(DAY_KEY, new Date().toISOString().slice(0, 10));
    } catch { /* storage disabled → still hide in-memory */ }
    setTimeout(() => setPopup(null), 300);
  };

  if (!popup) return null;

  // Label of the primary action button — falls back to a generic "OK" so a
  // CTA is ALWAYS visible even when the admin didn't configure a link.
  const ctaLabel = popup.link_text || t('home.view_all_short', 'OK');

  const renderCta = (className = 'rounded-none w-full', size) => (
    popup.link_url ? (
      <a href={popup.link_url} onClick={dismiss} data-testid="popup-cta-link">
        <Button className={className} size={size} data-testid="popup-cta-btn">{ctaLabel}</Button>
      </a>
    ) : (
      <Button className={className} size={size} onClick={dismiss} data-testid="popup-cta-btn">{ctaLabel}</Button>
    )
  );

  if (popup.style === 'banner') {
    return (
      <div
        data-testid="site-popup-banner"
        className={`fixed top-0 left-0 right-0 z-[60] bg-primary text-white transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`}
      >
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {popup.image_url && <img src={popup.image_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />}
            <div className="min-w-0">
              <p className="font-semibold text-sm">{popup.title}</p>
              {popup.message && <p className="text-xs opacity-90 truncate">{popup.message}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {renderCta('rounded-none h-7 text-xs', 'sm')}
            <button onClick={dismiss} className="p-1 hover:bg-white/20 rounded" data-testid="popup-close-btn" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (popup.style === 'slide') {
    return (
      <div
        data-testid="site-popup-slide"
        className={`fixed bottom-4 right-4 z-[60] max-w-sm bg-white border border-border shadow-xl transition-all duration-300 ${visible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
      >
        <div className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-bold text-sm">{popup.title}</h3>
            <button onClick={dismiss} className="p-0.5 hover:bg-gray-100 rounded" data-testid="popup-close-btn" aria-label="Fermer">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          {popup.image_url && <img src={popup.image_url} alt="" className="w-full h-32 object-cover mb-3" />}
          {popup.message && <p className="text-xs text-muted-foreground mb-3">{popup.message}</p>}
          {renderCta('rounded-none w-full h-8 text-xs', 'sm')}
        </div>
      </div>
    );
  }

  // Default: modal (with scroll-safe layout so the CTA never gets clipped)
  return (
    <div
      data-testid="site-popup-modal"
      className={`fixed inset-0 z-[60] flex items-center justify-center p-4 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    >
      <div className="absolute inset-0 bg-black/40" onClick={dismiss} />
      <div className={`relative bg-white max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl transition-transform duration-300 ${visible ? 'scale-100' : 'scale-95'}`}>
        <button onClick={dismiss} className="absolute top-3 right-3 p-1 hover:bg-gray-100 rounded z-10" data-testid="popup-close-btn" aria-label="Fermer">
          <X className="w-5 h-5 text-muted-foreground" />
        </button>
        {/* Scrollable body — image + text scroll if too tall */}
        <div className="overflow-y-auto flex-1">
          {popup.image_url && <img src={popup.image_url} alt="" className="w-full h-48 object-cover" />}
          <div className="p-6 pb-2">
            <h2 className="text-lg font-bold mb-2 pr-6">{popup.title}</h2>
            {popup.message && <p className="text-sm text-muted-foreground mb-2 whitespace-pre-wrap">{popup.message}</p>}
          </div>
        </div>
        {/* Sticky footer — CTA is ALWAYS visible regardless of body length */}
        <div className="p-6 pt-4 border-t border-gray-100 bg-white">
          {renderCta()}
        </div>
      </div>
    </div>
  );
};

export default SitePopup;
